import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { useSnackbar } from '../contexts/SnackbarProvider';
import GlobalError from '../components/GlobalError';
import SetDeviceUUID from '../components/SetDeviceUUID';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Stack } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useSocket from '../hooks/useSocket';

const DashboardPage = ({
  uuid,
  deviceSet,
  setUuid,
  setDeviceSet,
  warehouseId
}) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [count, setCount] = useState(0);
  const [error, setError] = useState(null);

  const fetchCount = async () => {
    if (!deviceSet || !warehouseId) return;

    setError(null);

    try {
      const res = await axiosInstance.post('/order/set', {
        device_ip: uuid,
        warehouse_id: warehouseId
      });

      setCount(res.data.count || 0);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      setError(err); // ← axiosのまま保持
    }
  };

  useEffect(() => {
    if (!deviceSet || !warehouseId) return;
    fetchCount();
  }, [deviceSet, warehouseId]);

  const handleSocketUpdate = (updatedOrder) => {
    if (updatedOrder.status === 'DONE') {
      fetchCount();
    }
  };

  useSocket(warehouseId, handleSocketUpdate);

  const handleResetDevice = async () => {
    if (!window.confirm('端末設定をリセットしますか？')) return;

    try {
      const res = await axiosInstance.post(
        '/picking/reset-device-uuid',
        {
          device_ip: uuid
        }
      );

      localStorage.removeItem('device_uuid');
      setUuid(null);
      setDeviceSet(false);
      setCount(0);
      setError(null);

      showSnackbar(res.data.message || '解除しました', 'success');
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar('解除に失敗しました', 'error');
    }
  };

  // ✅ DEVICE_INVALIDだけ特別扱い
  if (
    !deviceSet ||
    !warehouseId ||
    error?.response?.data?.code === 'DEVICE_INVALID'
  ) {
    return (
      <SetDeviceUUID
        onUuidSet={(newUuid) => {
          setUuid(newUuid);
          setDeviceSet(true);
          setError(null);
        }}
      />
    );
  }

  // ✅ その他エラー
  if (error) {
    return (
      <GlobalError
        errorMessage={
          error?.response?.data?.message || 'データの取得に失敗しました'
        }
      />
    );
  }

  return (
    <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', mt: 6 }}>
      <Typography variant="h4" gutterBottom>
        残ピッキング数
      </Typography>

      <Typography
        sx={{
          fontSize: '4rem',
          fontWeight: 900,
          lineHeight: 1,
          my: 1
        }}
      >
        {count}
      </Typography>

      <Button
        variant="contained"
        size="large"
        endIcon={<ChevronRightIcon />}
        onClick={() => navigate('/picking')}
        sx={{ maxWidth: 300 }}
      >
        ピッキングを始める
      </Button>

      <Button
        variant="outlined"
        color="error"
        size="small"
        onClick={handleResetDevice}
        sx={{ maxWidth: 200 }}
      >
        端末の紐づけを解除
      </Button>
    </Stack>
  );
};

export default DashboardPage;